import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Z } from "aldoo-ra/ZLayers"
import { ClockIcon } from "@heroicons/react/24/outline"
import { showErrorToast } from "aldoo-ra/Toast"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import useT from "../hooks/useT"
import { ExtendReservation } from "../storeApi"

const WARN_AT = 120 // seconds left when the countdown becomes visible
// the expired dialog's heading, as large as the other storefront dialogs' (Typography's own size gave way)
const EXPIRED_DIALOG = { title: "!text-[24px] md:!text-[26px] font-bold text-text text-center leading-tight my-0" }

/**
 * Countdown of the stock reservation held by the checkout's PaymentIntent
 * (fragment "checkout-extras", rendered inside the checkout modal). Appears in
 * the last two minutes with an "Extend" action. When it runs out the checkout
 * stays where it was: a dialog says the items were released and "Restore now"
 * makes the payment again — the same PaymentIntent, the stock held anew — so
 * the buyer carries on from the payment step.
 */
export default function ReservationToast({ store, refreshPayment }) {
  const t = useT()
  const paymentInfo = store.paymentInfo
  const expiresAt = paymentInfo?.reservationExpiresAt
  const paymentIntentId = paymentInfo?.paymentIntentId
  const [left, setLeft] = useState(null)
  const [extending, setExtending] = useState(false)
  // the reservation ran out: the dialog to restore it (a new one, restored or extended, ends it)
  const expired = !!expiresAt && left === 0

  useEffect(() => {
    if (!expiresAt) return setLeft(null)
    const tick = () => setLeft(Math.max(0, Math.round((new Date(expiresAt) - Date.now()) / 1000)))
    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [expiresAt])

  const restore = () => {
    // no way to make the payment again here (an older checkout): start over, as before
    if (!refreshPayment) return close()
    // the expired payment off the step (it shows "preparing payment"), then made again: the
    // stock held anew, or the step says what can no longer be had
    store.setPaymentInfo(null)
    refreshPayment()
  }
  const close = () => {
    store.setPaymentInfo(null)
    store.hideCheckout()
  }

  const extend = async () => {
    setExtending(true)
    try {
      const result = await ExtendReservation(paymentIntentId)
      if (result?.success && result.expiresAt) store.setPaymentInfo({ ...paymentInfo, reservationExpiresAt: result.expiresAt, ttlSeconds: result.ttlSeconds })
      else showErrorToast(t`Could not extend the reservation`, result?.reason || "")
    } finally {
      setExtending(false)
    }
  }

  if (expired)
    return (
      <Modal isOpen onClose={close} variant="sf_dialog" styleOverrides={EXPIRED_DIALOG} title={t`Are you still there?`} closeOnEscape>
        <div className="flex flex-col items-center gap-5 px-6 pt-2 pb-6" data-testid="reservation-expired">
          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
            <ClockIcon className="w-9 h-9 text-primary" />
          </span>
          <p className="text-[15px] text-text text-center m-0">{t`Restore your order to carry on where you left off.`}</p>
          {/* the one way on; the dialog's X (or Escape) closes the checkout */}
          <Button variant="primary" className="w-full" onClick={restore} data-testid="reservation-restore">
            {t`Recover now`}
          </Button>
        </div>
      </Modal>
    )

  if (left == null || left > WARN_AT || left <= 0) return null
  const mm = String(Math.floor(left / 60)).padStart(2, "0")
  const ss = String(left % 60).padStart(2, "0")

  // above everything, whatever dialog is open over the checkout (the office
  // picker is one): drawn at the page's root, in the band that beats every
  // dialog — inside the checkout it shared that dialog's layer and was covered
  return createPortal(
    // at the top of the screen, where it is seen (at the bottom it sat under the checkout's buttons)
    <div className="fixed top-4 left-1/2 flex w-max max-w-[calc(100vw-32px)] -translate-x-1/2 items-center gap-3 rounded-full border-2 border-primary bg-white py-2 pl-4 pr-2 text-text shadow-lg" style={{ zIndex: Z.modalTop }} role="status" data-testid="reservation-toast">
      <ClockIcon className="w-6 h-6 shrink-0 text-primary" />
      <span className="text-[14px]">
        {t`Your items are reserved for`} <strong className="font-mono text-primary">{mm}:{ss}</strong>
      </span>
      <Button variant="primary_s" className="shrink-0 !rounded-full" onClick={extend} disabled={extending}>
        {t`Extend`}
      </Button>
    </div>,
    document.body
  )
}
